import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  FaBoxOpen,
  FaTruck,
  FaExclamationTriangle,
  FaHome,
  FaArrowRight,
  FaBell,
  FaChartBar,
  FaUserClock,
  FaFileAlt,
  FaTag,
  FaStar,
} from 'react-icons/fa'
import { dashboardService } from '../services/dashboardService'

const StatCard = ({ icon: Icon, title, value, sub, color, link }) => {
  const colorMap = {
    primary: 'bg-[#3498db]',
    success: 'bg-[#27ae60]',
    warning: 'bg-[#e67e22]',
    info:    'bg-[#16a085]',
    danger:  'bg-[#e74c3c]',
    purple:  'bg-[#8e44ad]',
  }
  const card = (
    <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-6 flex items-center gap-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 group">
      <div className={`w-[60px] h-[60px] rounded-lg ${colorMap[color] || colorMap.primary} flex items-center justify-center flex-shrink-0`}>
        <Icon className="text-white text-xl" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#7f8c8d] text-sm mb-1">{title}</p>
        <p className="text-[#2c3e50] text-[1.8rem] font-bold leading-tight">{value}</p>
        {sub && <p className="text-[#95a5a6] text-xs mt-1 truncate">{sub}</p>}
        {link && (
          <div className="mt-1 text-[#3498db] text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            View Details <FaArrowRight className="text-[10px]" />
          </div>
        )}
      </div>
    </div>
  )
  return link ? <Link to={link}>{card}</Link> : card
}

const AlertBadge = ({ count, color = 'yellow' }) => {
  const colors = {
    yellow: 'bg-[#f39c12] text-white',
    blue:   'bg-[#3498db] text-white',
    purple: 'bg-[#8e44ad] text-white',
    red:    'bg-[#e74c3c] text-white',
  }
  return (
    <span className={`inline-flex items-center justify-center min-w-[26px] h-[26px] px-2 rounded-full text-xs font-bold ${colors[color]}`}>
      {count}
    </span>
  )
}

const SectionTitle = ({ title }) => (
  <h2 className="text-[1.1rem] font-semibold text-[#2c3e50] mb-5">{title}</h2>
)

export default function Dashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
    const interval = setInterval(fetchDashboardData, 60000)
    return () => clearInterval(interval)
  }, [])

  const fetchDashboardData = async () => {
    try {
      const result = await dashboardService.getDashboardSummary()
      if (result.success) {
        setData(result.data)
      } else {
        toast.error('Failed to fetch dashboard data')
      }
    } catch {
      toast.error('Error loading dashboard')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-[#3498db] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#7f8c8d] text-sm">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <p className="text-[#e74c3c]">Failed to load dashboard data</p>
      </div>
    )
  }

  const shipments         = data?.shipments?.summary || {}
  const shipmentsFunnel   = data?.shipments?.statusFunnel || []
  const overdue           = data?.shipments?.overdue || []
  const discountPending   = data?.discountRequests?.pending || 0
  const approvalsPending  = data?.users?.summary?.pending_trucker_approvals || 0
  const clearancePending  = data?.clearanceRequests?.submitted || 0
  const users             = data?.users?.summary || {}
  const brokers           = data?.brokers?.leaderboard || []
  const vehicles          = data?.vehicles || {}
  const cancellations     = data?.shipments?.cancellations || {}
  const platformSplit     = data?.shipments?.platformSplit || []
  const signupsByDay      = data?.users?.signupsByDay || []
  const clearanceBreakdown = data?.clearanceRequests || {}
  const notifications     = data?.notifications || {}

  const funnelMax = shipmentsFunnel[0]?.count || 1

  return (
    <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">

      {/* Page Header */}
      <div className="flex items-center justify-between pb-5 mb-8 border-b border-[#e9ecef]">
        <div className="flex items-center gap-3">
          <FaHome className="text-[#3498db] text-lg" />
          <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">Dashboard</h1>
        </div>
      </div>

      {/* Fleet Compliance Warning */}
      {vehicles?.complianceRate !== undefined && vehicles.complianceRate < 0.8 && (
        <div className="mb-8 p-5 bg-white border-l-4 border-[#e74c3c] rounded-lg shadow-sm flex gap-4">
          <FaExclamationTriangle className="text-[#e74c3c] text-xl flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-[#2c3e50] mb-2">Fleet Compliance Alert</h3>
            <p className="text-sm text-[#7f8c8d] mb-3">
              Only {Math.round(vehicles.complianceRate * 100)}% of fleet is fully compliant
            </p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-[#e74c3c]"><span className="font-semibold">{vehicles.missingRc || 0}</span> missing RC</div>
              <div className="text-[#e74c3c]"><span className="font-semibold">{vehicles.missingInsurance || 0}</span> missing insurance</div>
              <div className="text-[#e74c3c]"><span className="font-semibold">{vehicles.missingPermit || 0}</span> missing permits</div>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Volume Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard icon={FaBoxOpen}    title="Today"      value={shipments?.today?.count || 0}  sub={`${shipments?.today?.cancelled || 0} cancelled • PKR ${(shipments?.today?.value || 0).toLocaleString()}`}  color="primary" />
        <StatCard icon={FaChartBar}   title="This Week"  value={shipments?.week?.count || 0}   sub={`${shipments?.week?.cancelled || 0} cancelled • PKR ${(shipments?.week?.value || 0).toLocaleString()}`}   color="info"    />
        <StatCard icon={FaChartBar}   title="This Month" value={shipments?.month?.count || 0}  sub={`${shipments?.month?.cancelled || 0} cancelled • PKR ${(shipments?.month?.value || 0).toLocaleString()}`}  color="purple"  />
        <StatCard
          icon={FaTruck}
          title="Live Status"
          value={shipments?.live?.inTransit || 0}
          sub={`${shipments?.live?.pendingUnassigned || 0} pending • ${shipments?.live?.confirmedNoBroker || 0} confirmed`}
          color="success"
        />
      </div>

      {/* Action Required */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <Link to="/orders" className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-5 flex items-center justify-between hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <div className="flex items-center gap-3">
            <FaTag className="text-[#f39c12] text-lg" />
            <div>
              <p className="text-sm font-semibold text-[#2c3e50]">Discount Requests</p>
              <p className="text-xs text-[#7f8c8d]">Pending approval</p>
            </div>
          </div>
          <AlertBadge count={discountPending} color="yellow" />
        </Link>

        <Link to="/users" className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-5 flex items-center justify-between hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <div className="flex items-center gap-3">
            <FaUserClock className="text-[#3498db] text-lg" />
            <div>
              <p className="text-sm font-semibold text-[#2c3e50]">Trucker Approvals</p>
              <p className="text-xs text-[#7f8c8d]">Pending review</p>
            </div>
          </div>
          <AlertBadge count={approvalsPending} color="blue" />
        </Link>

        <Link to="/clearance" className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-5 flex items-center justify-between hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <div className="flex items-center gap-3">
            <FaFileAlt className="text-[#8e44ad] text-lg" />
            <div>
              <p className="text-sm font-semibold text-[#2c3e50]">Clearance Requests</p>
              <p className="text-xs text-[#7f8c8d]">Submitted for review</p>
            </div>
          </div>
          <AlertBadge count={clearancePending} color="purple" />
        </Link>
      </div>

      {/* Overdue Shipments */}
      {overdue.length > 0 && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-[#e9ecef] flex items-center gap-2">
            <FaExclamationTriangle className="text-[#e74c3c]" />
            <h2 className="text-[1rem] font-semibold text-[#2c3e50]">Overdue Shipments ({overdue.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e9ecef] bg-[#f8f9fa]">
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">ID</th>
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">Customer</th>
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">Due Date</th>
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">Days Overdue</th>
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {overdue.slice(0, 5).map((s) => (
                  <tr key={s.id} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa] transition-colors">
                    <td className="py-3 px-5 font-mono text-xs text-[#2c3e50]">{s.id}</td>
                    <td className="py-3 px-5 text-[#2c3e50]">{s.customer_name}</td>
                    <td className="py-3 px-5 text-[#7f8c8d]">{new Date(s.deliveryDate).toLocaleDateString()}</td>
                    <td className="py-3 px-5 font-semibold text-[#e74c3c]">{s.days_overdue}d</td>
                    <td className="py-3 px-5 text-[#7f8c8d] capitalize">{s.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Funnel + Platform Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">

        {/* Status Funnel */}
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-6">
          <SectionTitle title="Shipment Status Funnel" />
          <div className="space-y-4">
            {shipmentsFunnel.map((item, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-[#2c3e50]">{item.status}</span>
                  <span className="text-sm font-semibold text-[#2c3e50]">{item.count}</span>
                </div>
                <div className="w-full bg-[#ecf0f1] rounded-full h-2">
                  <div
                    className="bg-[#3498db] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (item.count / funnelMax) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Stats */}
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-6">
          <SectionTitle title="Platform Stats" />
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="p-4 bg-[#eaf4fb] rounded-lg text-center">
              <p className="text-xs text-[#3498db] mb-1">Customers</p>
              <p className="text-xl font-bold text-[#2c3e50]">{users?.customers || 0}</p>
            </div>
            <div className="p-4 bg-[#eafaf1] rounded-lg text-center">
              <p className="text-xs text-[#27ae60] mb-1">Truckers</p>
              <p className="text-xl font-bold text-[#2c3e50]">{users?.truckers || 0}</p>
            </div>
            <div className="p-4 bg-[#fef5e7] rounded-lg text-center">
              <p className="text-xs text-[#e67e22] mb-1">Drivers</p>
              <p className="text-xl font-bold text-[#2c3e50]">{users?.drivers || 0}</p>
            </div>
          </div>
          {vehicles?.complianceRate !== undefined && (
            <div className="pt-4 border-t border-[#e9ecef]">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-[#7f8c8d]">Fleet Compliance</p>
                <span className={`text-sm font-semibold ${vehicles.complianceRate >= 0.8 ? 'text-[#27ae60]' : 'text-[#e74c3c]'}`}>
                  {Math.round(vehicles.complianceRate * 100)}%
                </span>
              </div>
              <div className="w-full bg-[#ecf0f1] rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${vehicles.complianceRate >= 0.8 ? 'bg-[#27ae60]' : 'bg-[#e74c3c]'}`}
                  style={{ width: `${vehicles.complianceRate * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Broker Leaderboard */}
      {brokers.length > 0 && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-[#e9ecef] flex items-center gap-2">
            <FaStar className="text-[#f39c12]" />
            <h2 className="text-[1rem] font-semibold text-[#2c3e50]">Top Brokers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e9ecef] bg-[#f8f9fa]">
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">Broker</th>
                  <th className="text-right py-3 px-5 text-[#7f8c8d] font-medium">Delivered</th>
                  <th className="text-right py-3 px-5 text-[#7f8c8d] font-medium">Rating</th>
                  <th className="text-right py-3 px-5 text-[#7f8c8d] font-medium">Revenue (PKR)</th>
                </tr>
              </thead>
              <tbody>
                {brokers.slice(0, 10).map((b, idx) => (
                  <tr key={idx} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa] transition-colors">
                    <td className="py-3 px-5 font-medium text-[#2c3e50]">{b.broker_name}</td>
                    <td className="py-3 px-5 text-right text-[#7f8c8d]">{b.shipments_delivered}</td>
                    <td className="py-3 px-5 text-right">
                      <span className="text-[#f39c12]">★</span>{' '}
                      <span className="text-[#2c3e50]">{b.avg_rating?.toFixed(1) || 'N/A'}</span>
                    </td>
                    <td className="py-3 px-5 text-right font-semibold text-[#2c3e50]">{(b.total_revenue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cancellations */}
      {(cancellations.byActor?.length > 0 || cancellations.topReasons?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
          {cancellations.byActor?.length > 0 && (
            <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-6">
              <SectionTitle title="Cancellations by Actor" />
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e9ecef]">
                    <th className="text-left pb-3 text-[#7f8c8d] font-medium">Actor</th>
                    <th className="text-right pb-3 text-[#7f8c8d] font-medium">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellations.byActor.map((item, idx) => (
                    <tr key={idx} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa]">
                      <td className="py-2 text-[#2c3e50]">{item.cancelledBy}</td>
                      <td className="py-2 text-right font-semibold text-[#2c3e50]">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cancellations.topReasons?.length > 0 && (
            <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-6">
              <SectionTitle title="Top Cancellation Reasons" />
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e9ecef]">
                    <th className="text-left pb-3 text-[#7f8c8d] font-medium">Reason</th>
                    <th className="text-right pb-3 text-[#7f8c8d] font-medium">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellations.topReasons.slice(0, 5).map((item, idx) => (
                    <tr key={idx} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa]">
                      <td className="py-2 text-[#2c3e50]">{item.reason}</td>
                      <td className="py-2 text-right font-semibold text-[#2c3e50]">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Platform Split */}
      {platformSplit.length > 0 && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-[#e9ecef]">
            <h2 className="text-[1rem] font-semibold text-[#2c3e50]">Platform Split</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e9ecef] bg-[#f8f9fa]">
                  <th className="text-left py-3 px-5 text-[#7f8c8d] font-medium">Platform</th>
                  <th className="text-right py-3 px-5 text-[#7f8c8d] font-medium">Shipments</th>
                  <th className="text-right py-3 px-5 text-[#7f8c8d] font-medium">Total Value (PKR)</th>
                  <th className="text-right py-3 px-5 text-[#7f8c8d] font-medium">Avg Value</th>
                </tr>
              </thead>
              <tbody>
                {platformSplit.map((item, idx) => (
                  <tr key={idx} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa] transition-colors">
                    <td className="py-3 px-5 text-[#2c3e50] capitalize">{item.platform}</td>
                    <td className="py-3 px-5 text-right text-[#7f8c8d]">{item.count}</td>
                    <td className="py-3 px-5 text-right font-semibold text-[#2c3e50]">{(item.total_value || 0).toLocaleString()}</td>
                    <td className="py-3 px-5 text-right text-[#7f8c8d]">{item.count > 0 ? Math.round(item.total_value / item.count).toLocaleString() : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Growth */}
      {(users?.new_customers_week !== undefined || users?.new_truckers_week !== undefined) && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm mb-8 p-6">
          <SectionTitle title="User Growth (This Week)" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-[#eaf4fb] rounded-lg">
              <p className="text-xs text-[#3498db] mb-1">New Customers</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{users?.new_customers_week || 0}</p>
            </div>
            <div className="p-4 bg-[#eafaf1] rounded-lg">
              <p className="text-xs text-[#27ae60] mb-1">New Truckers</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{users?.new_truckers_week || 0}</p>
            </div>
            <div className="p-4 bg-[#fef5e7] rounded-lg">
              <p className="text-xs text-[#e67e22] mb-1">New Drivers</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{users?.new_drivers_week || 0}</p>
            </div>
            <div className="p-4 bg-[#fef9e7] rounded-lg">
              <p className="text-xs text-[#f39c12] mb-1">Not Onboarded</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{users?.drivers_not_onboarded || 0}</p>
            </div>
          </div>
          {signupsByDay.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e9ecef]">
                    <th className="text-left py-2 text-[#7f8c8d] font-medium">Date</th>
                    <th className="text-right py-2 text-[#7f8c8d] font-medium">Customers</th>
                    <th className="text-right py-2 text-[#7f8c8d] font-medium">Truckers</th>
                    <th className="text-right py-2 text-[#7f8c8d] font-medium">Drivers</th>
                  </tr>
                </thead>
                <tbody>
                  {signupsByDay.slice(0, 14).map((item, idx) => (
                    <tr key={idx} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa]">
                      <td className="py-2 text-[#2c3e50]">{new Date(item.date).toLocaleDateString()}</td>
                      <td className="py-2 text-right text-[#7f8c8d]">{item.customers || 0}</td>
                      <td className="py-2 text-right text-[#7f8c8d]">{item.truckers || 0}</td>
                      <td className="py-2 text-right text-[#7f8c8d]">{item.drivers || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Clearance Pipeline */}
      {(clearanceBreakdown?.draft !== undefined || clearanceBreakdown?.submitted !== undefined) && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm mb-8 p-6">
          <SectionTitle title="Clearance Request Pipeline" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Draft',     value: clearanceBreakdown?.draft || 0,      cls: 'bg-[#f8f9fa] border-[#e9ecef]',   txt: 'text-[#7f8c8d]' },
              { label: 'Submitted', value: clearanceBreakdown?.submitted || 0,  cls: 'bg-[#eaf4fb] border-[#3498db]',   txt: 'text-[#3498db]' },
              { label: 'Reviewed',  value: clearanceBreakdown?.reviewed || 0,   cls: 'bg-[#fef5e7] border-[#f39c12]',   txt: 'text-[#e67e22]' },
              { label: 'Rejected',  value: clearanceBreakdown?.rejected || 0,   cls: 'bg-[#fdedec] border-[#e74c3c]',   txt: 'text-[#e74c3c]' },
              { label: 'Total',     value: clearanceBreakdown?.total || 0,      cls: 'bg-[#f8f9fa] border-[#e9ecef]',   txt: 'text-[#7f8c8d]' },
            ].map(({ label, value, cls, txt }) => (
              <div key={label} className={`text-center p-4 border rounded-lg ${cls}`}>
                <p className={`text-xs mb-1 ${txt}`}>{label}</p>
                <p className="text-2xl font-bold text-[#2c3e50]">{value}</p>
              </div>
            ))}
          </div>
          {(clearanceBreakdown?.avgReviewDays !== undefined || clearanceBreakdown?.rejectionRate !== undefined) && (
            <div className="grid grid-cols-2 gap-4 mb-5">
              {clearanceBreakdown?.avgReviewDays !== undefined && (
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <p className="text-xs text-[#7f8c8d] mb-1">Avg Review Days</p>
                  <p className="text-xl font-bold text-[#2c3e50]">{Math.round(clearanceBreakdown.avgReviewDays * 10) / 10}</p>
                </div>
              )}
              {clearanceBreakdown?.rejectionRate !== undefined && (
                <div className="p-4 bg-[#f8f9fa] rounded-lg">
                  <p className="text-xs text-[#7f8c8d] mb-1">Rejection Rate</p>
                  <p className="text-xl font-bold text-[#2c3e50]">{clearanceBreakdown.rejectionRate}%</p>
                </div>
              )}
            </div>
          )}
          {clearanceBreakdown?.byType && (
            <div>
              <p className="text-sm font-semibold text-[#2c3e50] mb-3">By Type</p>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(clearanceBreakdown.byType).map(([type, count]) => (
                  <div key={type} className="p-3 bg-[#f8f9fa] rounded-lg text-center">
                    <p className="text-xs text-[#7f8c8d] capitalize mb-1">{type.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-bold text-[#2c3e50]">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notification Health */}
      {notifications?.total !== undefined && (
        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm mb-8 p-6">
          <div className="flex items-center gap-2 mb-5">
            <FaBell className="text-[#3498db]" />
            <h2 className="text-[1rem] font-semibold text-[#2c3e50]">Notification Health</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-[#eaf4fb] rounded-lg">
              <p className="text-xs text-[#3498db] mb-1">Total</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{notifications?.total || 0}</p>
            </div>
            <div className="p-4 bg-[#fdedec] rounded-lg">
              <p className="text-xs text-[#e74c3c] mb-1">Unread</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{notifications?.totalUnread || 0}</p>
            </div>
            <div className="p-4 bg-[#eafaf1] rounded-lg">
              <p className="text-xs text-[#27ae60] mb-1">Sent Last Week</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{notifications?.sentLastWeek || 0}</p>
            </div>
            <div className="p-4 bg-[#fef9e7] rounded-lg">
              <p className="text-xs text-[#f39c12] mb-1">Unread Last Week</p>
              <p className="text-2xl font-bold text-[#2c3e50]">{notifications?.unreadLastWeek || 0}</p>
            </div>
          </div>
          {notifications?.mostUnreadUsers?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-[#2c3e50] mb-3">Users with Most Unread</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e9ecef]">
                    <th className="text-left py-2 text-[#7f8c8d] font-medium">User</th>
                    <th className="text-left py-2 text-[#7f8c8d] font-medium">Role</th>
                    <th className="text-right py-2 text-[#7f8c8d] font-medium">Unread</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.mostUnreadUsers.slice(0, 5).map((u, idx) => (
                    <tr key={idx} className="border-b border-[#e9ecef] hover:bg-[#f8f9fa]">
                      <td className="py-2 text-[#2c3e50]">{u.name}</td>
                      <td className="py-2 text-[#7f8c8d] capitalize">{u.role}</td>
                      <td className="py-2 text-right font-semibold text-[#2c3e50]">{u.unread_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  )
}
